import { Plus, Sheet, SquareArrowOutUpRight } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { IgAnalyticsTooltip } from "@intelligems/ig-theme/IgAnalyticsTooltip";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { ExperienceAnalysisDto } from "@intelligems/ig-types/dtos/experience-analysis";
import {
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
  StandardEventAllowedKeyMetrics,
} from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { shortId } from "@intelligems/ig-utils/common";
import { CircularProgress, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { usePutConfigureAnalytics } from "@src/api/analytics/put-configure-analytics";
import {
  getMetricCategoryName,
  getTabName,
} from "@src/features/analytics/tables/analyticsAudienceTable/utils";
import { useDispatch, useSelector } from "@src/store";
import {
  areAnalyticsProductsLoaded,
  selectedAnalyticsProductsState,
} from "@src/store/slices/analyticsProducts";
import {
  getExperienceAnalyticsPageName,
  updateDashboardFilter,
} from "@src/store/slices/dashboardFilters";
import {
  getCustomMetrics,
  getKeyMetricsRich,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import {
  mpAnalyticsMetricDetailClick,
  mpClickAddMetric,
  mpClickSubpageEntryPoint,
} from "@src/utils/mixpanel";
import { useIsMutating } from "@tanstack/react-query";

interface GraphHeaderToolbarProps {
  config: GraphConfig;
  experienceId: string;
  orgId: string;
  hover: boolean;
}

export const GraphHeaderToolbar = ({
  config,
  experienceId,
  orgId,
  hover,
}: GraphHeaderToolbarProps) => {
  const pageName = useSelector(getExperienceAnalyticsPageName);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const experience = useSelector(selectedExperience);
  const experienceCustomMetrics = useSelector(getCustomMetrics);
  const experienceKeyMetrics = useSelector(getKeyMetricsRich);
  const analyticsProductsState = useSelector(selectedAnalyticsProductsState);
  const allProductsLoaded = useSelector(areAnalyticsProductsLoaded);
  const isPutMutating = useIsMutating({
    mutationKey: ["put-configure-analytics"],
  });
  const putConfigureAnalytics = usePutConfigureAnalytics({
    productsLocation: analyticsProductsState.productsLocation || undefined,
    removeAllProducts:
      analyticsProductsState.storedAnalyticsProducts.length > 0 &&
      analyticsProductsState.analyticsProducts.length === 0,
    allProductsLoaded,
    experienceId: experience.id!,
    analyticsProducts: analyticsProductsState.analyticsProducts || [],
  });

  const isAlreadyKeyMetric = () => {
    const metricName = config.metrics[0];
    const isCustomMetricsFlow = pageName === "customMetrics";

    if (isCustomMetricsFlow) {
      return experienceKeyMetrics.find(
        (keyMetric) =>
          keyMetric.experienceCustomMetric?.customEvent?.identifier ===
          metricName
      );
    } else {
      const standardEvent = findMetricByAnalyticsId(
        metricName as StandardEventAllowedKeyMetrics
      );
      if (!standardEvent?.key) {
        return false;
      }

      return experienceKeyMetrics.find(
        (keyMetric) =>
          keyMetric.standardEventId === standardEvent.metricObject.id
      );
    }
  };

  const findMetricByAnalyticsId = (
    analyticsMetricId: StandardEventAllowedKeyMetrics
  ) => {
    for (const key in STANDARD_EVENTS_ALLOWED_KEY_METRICS) {
      if (
        STANDARD_EVENTS_ALLOWED_KEY_METRICS[key].analyticsMetricId ===
        analyticsMetricId
      ) {
        return {
          key: key,
          metricObject: STANDARD_EVENTS_ALLOWED_KEY_METRICS[key],
        };
      }
    }
    return null;
  };

  const handleAddKeyMetric = () => {
    const metricName = config.metrics[0];
    const isCustomMetricsFlow = pageName === "customMetrics";

    let newKeyMetric;

    if (isCustomMetricsFlow) {
      const foundCustomMetric = experienceCustomMetrics.find(
        (customMetric) => shortId(customMetric.customEvent.id) === metricName
      );
      if (!foundCustomMetric) {
        return;
      }

      newKeyMetric = {
        order: experienceKeyMetrics.length,
        experienceCustomMetricId: foundCustomMetric.id,
        experienceId: experience.id,
      };
      mpClickAddMetric(
        experienceId,
        "Subpage Metric",
        foundCustomMetric.customEvent.identifier
      );
    } else {
      const standardEvent = findMetricByAnalyticsId(
        metricName as StandardEventAllowedKeyMetrics
      );
      if (!standardEvent?.key) {
        return;
      }

      newKeyMetric = {
        order: experienceKeyMetrics.length,
        standardEventId: standardEvent.metricObject.id,
        experienceId: experience.id,
      };
      mpClickAddMetric(
        experienceId,
        "Subpage Metric",
        standardEvent.metricObject.analyticsMetricId
      );
    }

    const updatedKeyMetrics = [...experienceKeyMetrics, newKeyMetric];

    putConfigureAnalytics.mutate(
      {
        experienceCustomMetrics,
        experienceKeyMetrics: updatedKeyMetrics,
        experienceAnalysis:
          experience.experienceAnalysis as ExperienceAnalysisDto,
        productsLocation: analyticsProductsState.productsLocation,
      },
      {
        onSuccess: () => {
          toast.success("Successfully added as a Key Metric!");
        },
      }
    );
  };

  const onExploreMetricClick = () => {
    mpClickSubpageEntryPoint(
      experienceId,
      "Graph Toolbar",
      getTabName(config.metrics[0]),
      config?.impactMetric ?? config.metrics[0]
    );
    dispatch(
      updateDashboardFilter({
        key: "pageName",
        value: getTabName(config.metrics[0]),
      })
    );
  };

  const shouldHideExploreButton = (
    metricId: string,
    pageName: string
  ): boolean => {
    if (pageName !== "overview") {
      return true;
    }
    const categoryName = getMetricCategoryName(metricId);

    const categoryToPageMap: Record<string, string[]> = {
      Conversion: ["conversion"],
      Revenue: ["revenue"],
      Profit: ["profit"],
      Shipping: ["shipping"],
      Subscription: ["subscriptions"],
      Order: ["orders"],
      Custom: ["customMetrics"],
      Discount: ["discount"],
    };

    const relatedPages = categoryToPageMap[categoryName] || [];
    return relatedPages.includes(pageName);
  };

  if (!hover) {
    return null;
  }

  if (isPutMutating) {
    return <CircularProgress size={"30px"} />;
  }

  return (
    <Grid sx={{ position: "absolute", top: "0", right: "0", zIndex: 1 }}>
      <IgPaper
        sx={{
          padding: "5px 5px",
          borderRadius: "10px",
          boxShadow: theme.igShadows.low,
        }}
      >
        <Grid
          container
          direction={window.innerWidth <= 2500 ? "column" : "row"}
          columnSpacing={0.8}
          wrap={"nowrap"}
        >
          {/*<Grid>*/}
          {/*  <IgAnalyticsTooltip title={"Share"}>*/}
          {/*    <IgButton*/}
          {/*      size={"small"}*/}
          {/*      onClick={() => {*/}
          {/*        dispatch(*/}
          {/*          openDialog({*/}
          {/*            dialog: "share",*/}
          {/*            props: {*/}
          {/*              id: experienceId,*/}
          {/*              orgId,*/}
          {/*              title: config.title,*/}
          {/*              datasetId: config.datasetId,*/}
          {/*              isImpact: config.isImpactGraph,*/}
          {/*            },*/}
          {/*          })*/}
          {/*        );*/}
          {/*        mpClickShare(experienceId);*/}
          {/*      }}*/}
          {/*      sx={{*/}
          {/*        minWidth: "25px !important",*/}
          {/*        minHeight: "25px !important",*/}
          {/*        color: ColorTextFill.hint,*/}
          {/*        borderRadius: "5px",*/}
          {/*        padding: "5px !important",*/}
          {/*        "&:hover": {*/}
          {/*          color: ColorPrimary.main,*/}
          {/*          backgroundColor: "#E9F4FE",*/}
          {/*        },*/}
          {/*      }}*/}
          {/*      variant={"text"}*/}
          {/*    >*/}
          {/*      <ShareDetailsIcon sx={{ fontSize: "15px" }} />*/}
          {/*    </IgButton>*/}
          {/*  </IgAnalyticsTooltip>*/}
          {/*</Grid>*/}
          <Grid>
            <IgAnalyticsTooltip title={"Metric Details"}>
              <IgButton
                onClick={() => {
                  mpAnalyticsMetricDetailClick(
                    experienceId,
                    "Graph Toolbar",
                    config?.impactMetric ?? config.metrics[0]
                  );
                  void navigate(location.pathname + location.search, {
                    state: {
                      targetId: "metric-details-wrapper",
                      targetMetricId: config?.impactMetric ?? config.metrics[0],
                    },
                    replace: true,
                  });
                }}
                sx={{
                  minWidth: "25px !important",
                  minHeight: "25px !important",
                  borderRadius: "5px",
                  padding: "5px !important",
                }}
                variant={"text"}
              >
                <Sheet size={20} />
              </IgButton>
            </IgAnalyticsTooltip>
          </Grid>
          {config.canBeKeyMetric &&
          pageName !== "overview" &&
          experienceKeyMetrics.length < 7 &&
          !isAlreadyKeyMetric() ? (
            <Grid>
              <IgAnalyticsTooltip title={"Add as Key Metric"}>
                <IgButton
                  sx={{
                    minWidth: "25px !important",
                    minHeight: "25px !important",
                    borderRadius: "5px",
                    padding: "5px !important",
                  }}
                  variant={"text"}
                  onClick={handleAddKeyMetric}
                >
                  <Plus size={24} />
                </IgButton>
              </IgAnalyticsTooltip>
            </Grid>
          ) : null}
          {!shouldHideExploreButton(config.metrics[0], pageName) && (
            <Grid>
              <IgAnalyticsTooltip
                title={`Explore ${getMetricCategoryName(config.metrics[0])} Metrics`}
              >
                <IgButton
                  onClick={onExploreMetricClick}
                  sx={{
                    minWidth: "25px !important",
                    minHeight: "25px !important",
                    borderRadius: "5px",
                    padding: "5px !important",
                  }}
                  variant={"text"}
                >
                  <SquareArrowOutUpRight size={20} />
                </IgButton>
              </IgAnalyticsTooltip>
            </Grid>
          )}
        </Grid>
      </IgPaper>
    </Grid>
  );
};
